import React, { useEffect } from "react";

export function PageMeta(props) {
  const { title, description } = props;
  useEffect(() => {
    const fullTitle = title ? title + " · PsyTech" : "PsyTech";
    document.title = fullTitle;
    const setMeta = (name, content, attr) => {
      if (!content) return;
      let tag = document.head.querySelector("meta[" + attr + "=\"" + name + "\"]");
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    if (description) {
      setMeta("description", description, "name");
      setMeta("og:title", fullTitle, "property");
      setMeta("og:description", description, "property");
      setMeta("og:type", "website", "property");
    }
    window.scrollTo(0, 0);
  }, [title, description]);
  return null;
}

export function PageHero({ eyebrow, title, subtitle, children }) {
  return (
    <section className="relative overflow-hidden hero-gradient">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-cyan-600/10 rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(rgba(14,116,144,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(14,116,144,0.05)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {eyebrow && (
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400 mb-4">
            {eyebrow}
          </span>
        )}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight font-['Plus_Jakarta_Sans']">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
            {subtitle}
          </p>
        )}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </section>
  );
}

export function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`py-20 md:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, center = false }) {
  return (
    <div className={`mb-12 ${center ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`}>
      {eyebrow && (
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 mb-3">
          {eyebrow}
        </span>
      )}
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] leading-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

export function FeatureCard({ icon: Icon, title, children }) {
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-cyan-300 hover:shadow-lg hover:shadow-cyan-900/5 transition-all">
      {Icon && (
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/10 ring-1 ring-cyan-200 flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-cyan-700" />
        </div>
      )}
      <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
        {title}
      </h3>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

export function Pill({ children, tone = "cyan" }) {
  const tones = {
    cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    slate: "bg-slate-50 text-slate-600 ring-slate-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function DisclaimerBanner() {
  return (
    <div className="bg-slate-50 border-y border-slate-200">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
        <p className="text-xs text-slate-500 leading-relaxed">
          <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
          It does not provide diagnosis, treatment, medication advice, or emergency support.
          AI-generated insights should be reviewed by qualified professionals before clinical use.
        </p>
      </div>
    </div>
  );
}

export function CtaBlock({ title, subtitle, primaryHref, primaryLabel, secondaryHref, secondaryLabel, testid }) {
  const isExternal = (h) => h && /^https?:\/\//.test(h);
  return (
    <section className="py-20 md:py-24 bg-slate-900 relative overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(6,182,212,0.15),transparent_60%)]" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white font-['Plus_Jakarta_Sans'] leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-4 text-base md:text-lg text-slate-300 max-w-2xl mx-auto">{subtitle}</p>
        )}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {primaryHref && (
            isExternal(primaryHref) ? (
              <a
                href={primaryHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-cyan-600 hover:bg-cyan-500 text-white px-7 py-3 text-sm font-semibold transition-all shadow-lg shadow-cyan-900/30 hover:scale-[1.02]"
              >
                {primaryLabel}
              </a>
            ) : (
              <a
                href={primaryHref}
                className="inline-flex items-center justify-center rounded-full bg-cyan-600 hover:bg-cyan-500 text-white px-7 py-3 text-sm font-semibold transition-all shadow-lg shadow-cyan-900/30 hover:scale-[1.02]"
              >
                {primaryLabel}
              </a>
            )
          )}
          {secondaryHref && (
            <a
              href={secondaryHref}
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-7 py-3 text-sm font-semibold transition-all backdrop-blur"
            >
              {secondaryLabel}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
